import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessagesSquare, Heart, MessageCircle, Plus, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CommunityPage() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "general", title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/community/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = category ? { category } : {};
    api.get("/community/posts", { params }).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, [category]);

  const toggleLike = async (postId) => {
    const { data } = await api.post(`/community/posts/${postId}/like`);
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, like_count: data.like_count, liked_by_me: data.liked_by_me } : p)));
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/community/posts", form);
      if (!category || category === form.category) setPosts((prev) => [data, ...prev]);
      setForm({ category: "general", title: "", body: "" });
      setShowForm(false);
      toast.success("Post published!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't publish your post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="community-page">
      <div className="flex items-center gap-3 mb-2">
        <MessagesSquare className="w-6 h-6 text-indigo-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Community</span>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Ask, Share, Learn Together</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">Post questions, share notes and SQL snippets, or celebrate a win with fellow learners.</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} data-testid="community-new-post-btn" className="shrink-0 flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showForm ? "Cancel" : "New Post"}
        </button>
      </div>

      {showForm && (
        <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} onSubmit={createPost} className="glass-panel rounded-2xl p-6 mt-6 space-y-3" data-testid="community-new-post-form">
          <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="community-post-category-select" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title" data-testid="community-post-title-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
          <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder="Share the details..." rows={4} data-testid="community-post-body-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 resize-none" />
          <button type="submit" disabled={submitting} data-testid="community-submit-post-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish"}
          </button>
        </motion.form>
      )}

      <div className="flex items-center gap-2 mt-8 overflow-x-auto pb-1" data-testid="community-category-filters">
        <button onClick={() => setCategory(null)} data-testid="community-category-all" className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${!category ? "bg-indigo-400/15 border-indigo-400/40 text-indigo-300" : "border-white/10 text-slate-400 hover:text-white"}`}>All</button>
        {categories.map((c) => (
          <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`community-category-${c.id}`} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${category === c.id ? "bg-indigo-400/15 border-indigo-400/40 text-indigo-300" : "border-white/10 text-slate-400 hover:text-white"}`}>{c.label}</button>
        ))}
      </div>

      <div className="space-y-3 mt-5">
        {loading ? (
          <div className="flex justify-center py-16" data-testid="community-posts-loading"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="glass-panel rounded-2xl p-10 text-center text-slate-400" data-testid="community-posts-empty">No posts yet in this category — be the first to share!</div>
        ) : posts.map((p) => (
          <div key={p.id} className="glass-panel rounded-2xl p-5" data-testid={`community-post-${p.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-white overflow-hidden">
                {p.user_picture ? <img src={p.user_picture} alt={p.user_name} loading="lazy" className="w-full h-full object-cover" /> : p.user_name?.[0]}
              </div>
              <span className="text-sm text-white font-medium">{p.user_name}</span>
              {p.is_mentor && <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" title="Mentor" />}
              <span className="text-xs text-slate-500 ml-auto">{new Date(p.created_at).toLocaleDateString()}</span>
            </div>
            <Link to={`/community/${p.id}`} data-testid={`community-post-title-link-${p.id}`} className="block">
              <h3 className="font-heading text-base font-semibold text-white hover:text-indigo-300 transition-colors">{p.title}</h3>
              <p className="text-sm text-slate-400 mt-1 line-clamp-2">{p.body}</p>
            </Link>
            <div className="flex items-center gap-4 mt-3">
              <button onClick={() => toggleLike(p.id)} data-testid={`community-like-btn-${p.id}`} className={`flex items-center gap-1.5 text-xs font-semibold ${p.liked_by_me ? "text-rose-400" : "text-slate-500 hover:text-rose-300"}`}>
                <Heart className={`w-3.5 h-3.5 ${p.liked_by_me ? "fill-rose-400" : ""}`} /> {p.like_count}
              </button>
              <Link to={`/community/${p.id}`} data-testid={`community-reply-link-${p.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-300">
                <MessageCircle className="w-3.5 h-3.5" /> {p.reply_count}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
